/*!
 * Copyright (c) 2021, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { View , loc } from '@okta/courage';
import Enums from 'util/Enums';
import Util from 'util/Util';
import hbs from '@okta/handlebars-inline-precompile';
import { WIDGET_FOOTER_CLASS } from '../../utils/Constants';

const OktaSignInWidgetOnCaptchaLoadedCallback = 'OktaSignInWidgetOnCaptchaLoaded';
const OktaSignInWidgetOnCaptchaSolvedCallback = 'OktaSignInWidgetOnCaptchaSolved';

const HCAPTCHA_URL = 'https://hcaptcha.com/1/api.js';
// ALTCHA_URL and ALTCHA_SRI_HASH must be updated together — changing one
// without the other will silently break CAPTCHA.
//
// To regenerate the SRI hash after a version bump:
//   echo "sha384-$(curl -s <new-url> | openssl dgst -sha384 -binary | openssl base64 -A)"
const ALTCHA_URL = 'https://cdn.jsdelivr.net/gh/altcha-org/altcha@2.3.0/dist/altcha.min.js';
const ALTCHA_SRI_HASH = 'sha384-lxB6k+TvdhSBKnLm6JqwAnW7QxKXj88yK1kq9g3MevDXlG9HdWtnShLgJzuYCUIw';
const RECAPTCHAV2_URL = 'https://www.google.com/recaptcha/api.js';

const ALTCHA_CHALLENGE_PATH = '/api/v1/altcha';

export default View.extend({
  className: 'captcha-view',

  template: hbs`
    {{#if isCaptchaConfigured}}\
      <div id ="captcha-container"
          class="{{className}}"
          data-sitekey="{{siteKey}}"
          data-callback="{{onCaptchaSolvedCallback}}"
          data-size="invisible">
      </div>
    {{/if}}\
  `,

  getTemplateData: function() {
    if (this.captchaConfig) {
      let className;

      if (this.captchaConfig.type === 'ALTCHA') {
        className = 'altcha-captcha';
      } else if (this.captchaConfig.type === 'HCAPTCHA') {
        className = 'h-captcha';
      } else {
        className = 'g-recaptcha';
      }

      return { 
        siteKey: this.captchaConfig.siteKey,
        isCaptchaConfigured: true,
        onCaptchaSolvedCallback: OktaSignInWidgetOnCaptchaSolvedCallback,
        className, 
      };
    } else {
      return {};
    }
  },

  initialize() {
    if (this.options.appState.get('captcha')) {
      this.captchaConfig = this.options.appState.get('captcha');
      this._addCaptcha();
    }
  },

  remove: function(){
    View.prototype.remove.apply(this, arguments);

    // Cleanup global Captcha references
    if (this.captchaConfig.type === 'HCAPTCHA') {
      window.hcaptcha = undefined;
    } else if (this.captchaConfig.type === 'RECAPTCHA_V2') {
      window.grecaptcha = undefined;
    }
  },

  /**
   *  Load the CAPTCHA lib dynamically (either HCAPTCHA or RECAPTCHAV2). Once loaded, trigger an event to inform
   *  the parent form to actually render the CAPTCHA.
  * */   
  _addCaptcha() {
    // Base callback invoked when CAPTCHA is solved.
    const onCaptchaSolvedBase = (token) => {
      const captchaObject = this._getCaptchaOject();

      // We reset the Captcha. We need to reset because every time the 
      // Captcha resolves back with a token and say we have a server side error, 
      // if we submit the form again it won't work otherwise. The Captcha 
      // has to be reset for it to work again in that scenario.
      const captchaId = this.$el.find('#captcha-container').attr('data-captcha-id');
      captchaObject.reset(captchaId);

      // Set the token in the model
      const fieldName = this.options.name;
      this.model.set(fieldName, token);
    };

    // Callback invoked when reCAPTCHA or HCaptcha is solved.
    const onReCaptchaOrHCaptchaSolved = (token) => {
      onCaptchaSolvedBase(token);

      // Calls saveForm on the BaseForm to trigger the form submission flow.
      // IdentifierView.saveForm ran BEFORE hCAPTCHA and reCAPTCHA loaded
      this.options.appState.trigger('saveForm', this.model); 
    };

    // Callback invoked when ALTCHA is solved.
    const onAltchaSolved = (token) => {
      onCaptchaSolvedBase(token);

      // Calls saveForm on the parent view (ex. IdentifierView) to trigger the form submission flow.
      // This ensures ParentView.saveForm() runs (for deviceFingerprint, etc. if applicable)
      // and the parent view triggers BaseForm.saveForm().
      this.options.appState.trigger('altchaSolved');
    };

    const setUpTempToken = () => {
      // This is just a safeguard to ensure we don't bind Captcha to an already bound element.
      // It shouldn't happen in practice.
      if (this.$el.find('#captcha-container').attr('data-captcha-id')) {
        return;
      }

      // We set a temporary token for Captcha because this is a required field for the form and is normally set
      // at a later time. In order to prevent client-side validation errors because of this, we have to set a 
      // dummy value. We then overwrite this with the proper token in the onCaptchaSolved callback.
      this.model.set(this.options.name, 'tempToken');
    };

    const getAltchaWidgetStrings = () => ({
      error: loc('altcha.error.label', 'login'),
      expired: loc('altcha.expired.label', 'login'),
      label: loc('altcha.label.label', 'login'),
      loading: loc('altcha.loading.label', 'login'),
      reload: loc('altcha.reload.label', 'login'),
      verify: loc('altcha.verify.label', 'login'),
      verificationRequired: loc('altcha.verificationRequired.label', 'login'),
      verified: loc('altcha.verified.label', 'login'),
      verifying: loc('altcha.verifying.label', 'login'),
      waitAlert: loc('altcha.waitAlert.label', 'login'),
    });

    const altchaCustomFetch = async (url, init) => {
      const { challengeUrlForm } = this.captchaConfig;

      // If we do not have a challengeUrlForm, we can return window.fetch immediately
      if (!challengeUrlForm) {
        // eslint-disable-next-line compat/compat, no-undef
        return fetch(url, init);
      }

      const { accepts, method, value } = challengeUrlForm;

      const newInit = { ...init };

      if (accepts) {
        newInit.headers = { ...newInit.headers, 'Content-Type': accepts };
      }

      if (method) {
        newInit.method = method;
      }

      // Check if the state handle is one of the fields that should be passed in the body
      if (value?.find((field) => field?.name === 'stateHandle')) {
        newInit.body = JSON.stringify({ 
          stateHandle: this.options.appState.get('idx').context.stateHandle
        });
      }

      // eslint-disable-next-line compat/compat, no-undef
      return fetch(url, newInit);
    };

    const onAltchaCaptchaLoaded = () => {
      setUpTempToken();

      const $container = this.$el.find('#captcha-container');
      const altEl = document.createElement('altcha-widget');

      const challengeUrlFallback = `${this.options.settings.get('baseUrl') ?? ''}${ALTCHA_CHALLENGE_PATH}`;

      const { challengeUrlForm } = this.captchaConfig;

      Object.entries({
        floating: true,
        hidefooter: true,
        hidelogo: true,
        challengeurl: challengeUrlForm?.href ?? challengeUrlFallback,
        strings: JSON.stringify(getAltchaWidgetStrings())
      }).forEach(([key, value]) => altEl.setAttribute(key, value));

      altEl.customfetch = altchaCustomFetch;

      if (altEl.addEventListener) {
        altEl.addEventListener('verified', (e) => {
          const token = (e && e.detail && e.detail.payload) || e;
          onAltchaSolved(token);
 
          $container.empty();
        });
      }

      window.altcha = altEl;
      $container.empty().append(altEl);
      // mark as bound
      $container.attr('data-captcha-id', 'altcha');
      // notify Baseform
      this.options.appState.trigger('onCaptchaLoaded', altEl);
    };

    // Callback when CAPTCHA lib is loaded
    const onReCaptchaOrHCaptchaLoaded = () => {
      setUpTempToken();

      const captchaObject = this._getCaptchaOject();

      const captchaId = captchaObject.render('captcha-container', {
        sitekey: this.captchaConfig.siteKey,
        callback: onReCaptchaOrHCaptchaSolved
      });
      
      this.$el.find('#captcha-container').attr('data-captcha-id', captchaId);

      // Let the Baseform know that Captcha is loaded.
      this.options.appState.trigger('onCaptchaLoaded', captchaObject);

      // Render the HCAPTCHA footer - we need to do this manually since the HCAPTCHA lib doesn't do it
      if (this.captchaConfig.type === 'HCAPTCHA') {
        this._addHCaptchaFooter();
      }  
    };

    // Attaching the callback to the window object so that the CAPTCHA script that we dynamically render
    // can have access to it since it won't have access to this view's scope.
    if(this.captchaConfig.type === 'ALTCHA') {
      window[OktaSignInWidgetOnCaptchaLoadedCallback] = onAltchaCaptchaLoaded;
    } else {
      window[OktaSignInWidgetOnCaptchaLoadedCallback] = onReCaptchaOrHCaptchaLoaded;
    }

    // Attaching the Captcha solved callback to the window object because we reference it in our template under
    // the 'data-callback' attribute which the Captcha library uses to invoke the callback.
    window[OktaSignInWidgetOnCaptchaSolvedCallback] = onReCaptchaOrHCaptchaSolved;


    if (this.captchaConfig.type === 'HCAPTCHA') {
      this._loadCaptchaLib(this._getCaptchaUrl(HCAPTCHA_URL, 'hcaptcha'));
    } else if (this.captchaConfig.type === 'RECAPTCHA_V2') {
      this._loadCaptchaLib(this._getCaptchaUrl(RECAPTCHAV2_URL, 'recaptcha'));
    } if (this.captchaConfig.type === 'ALTCHA') {
      this._loadCaptchaLib(this._getCaptchaUrl(ALTCHA_URL, 'altcha'));
    }
  },
  
  /**
   *  We dynamically inject <script> tag into our login container because in case the customer is hosting
   *  the SIW, we need to ensure we don't go out of scope when injecting the script.
  * */ 
  _loadCaptchaLib(url) {
    let scriptTag = document.createElement('script');
    scriptTag.src = url;
    scriptTag.async = true;
    scriptTag.defer = true;

    if (url.indexOf('altcha') !== -1) {
      scriptTag.type = 'module';
      if (this.captchaConfig?.sriEnabled) {
        scriptTag.integrity = ALTCHA_SRI_HASH;
        scriptTag.crossOrigin = 'anonymous';
      }
      scriptTag.onload = window[OktaSignInWidgetOnCaptchaLoadedCallback];
    }

    document.getElementById(Enums.WIDGET_CONTAINER_ID).appendChild(scriptTag);
  },

  _addHCaptchaFooter() {
    // NOTE: insetAdjacentHTML() is supported in all major browsers: 
    // https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentHTML#browser_compatibility
    const footerContainer = document.getElementsByClassName(WIDGET_FOOTER_CLASS);
    if (footerContainer.length) {
      const template = hbs('<div class="captcha-footer">\
        <span class="footer-text">\
          {{i18n code="hcaptcha.footer.label" bundle="login" \
          $1="<a href=\'https://hcaptcha.com/privacy\' target=\'_blank\'>$1</a>" \
          $2="<a href=\'https://hcaptcha.com/terms\' target=\'_blank\'>$2</a>"}}\
        </span>\
      </div>');

      footerContainer[0].insertAdjacentHTML('beforeend',
        template()
      );
    }
  },

  _getCaptchaOject() {
    // Return the appropriate global captcha object for the configured type.
    if (this.captchaConfig.type === 'HCAPTCHA') {
      return window.hcaptcha;
    }
    if (this.captchaConfig.type === 'RECAPTCHA_V2') {
      return window.grecaptcha;
    }
    if (this.captchaConfig.type === 'ALTCHA') {
      // Altcha may expose different globals or be used as a web component.
      return window.altcha ?? null;
    }
    return null;
  },

  /**
   *  Supported params for hCaptcha script:
   *   https://github.com/hCaptcha/hcaptcha-loader#props
   *   (starting from 'apihost')
   *  Supported params for reCAPTCHA script:
   *   https://developers.google.com/recaptcha/docs/display#javascript_resource_apijs_parameters
  * */
  _getCaptchaUrl(defaultBaseUrl, settingsKey) {
    const locale = this.options.settings.get('language');
    const scriptSource = this.options.settings.get(`${settingsKey}.scriptSource`);
    const scriptParams = this.options.settings.get(`${settingsKey}.scriptParams`);

    const baseUrl = scriptSource || defaultBaseUrl;

    // Altcha is a web component / module; don't append reCAPTCHA/hCaptcha-specific query params.
    if (settingsKey === 'altcha') {
      return baseUrl;
    }

    const params = {
      ...scriptParams,
      onload: OktaSignInWidgetOnCaptchaLoadedCallback,
      render: 'explicit',
      hl: locale || navigator.language,
    };
    const query = Util.searchParamsToString(params);
    return baseUrl + '?' + query;
  },

});
